import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, CalendarPlus, ChevronDown, ChevronUp } from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import { toYmd } from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import { toLocalInputValue } from '../utils/noticeRichUtils';
import {
  findDisplayWindowConflict,
  localPartsToDate,
  endOfLocalDay,
  slotTouchesLocalDay,
} from '../utils/noticeScheduleConflict';
import CustomersNoticeTip from './CustomersNoticeTip';
import './CustomersNoticeScheduleDates.css';

const WEEKDAYS = ['do', '2ª', '3ª', '4ª', '5ª', '6ª', 'sá'];
const MONTHS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function splitLocal(value, fallbackHour = '09', fallbackMinute = '00') {
  if (!value || !String(value).includes('T')) {
    return { date: null, hour: fallbackHour, minute: fallbackMinute };
  }
  const [date, time = `${fallbackHour}:${fallbackMinute}`] = String(value).split('T');
  const [h = fallbackHour, m = fallbackMinute] = time.split(':');
  return {
    date: date || null,
    hour: pad2(Math.min(23, Math.max(0, parseInt(h, 10) || 0))),
    minute: pad2(Math.min(59, Math.max(0, parseInt(m, 10) || 0))),
  };
}

function joinLocal(date, hour, minute) {
  if (!date) return '';
  return `${date}T${pad2(hour)}:${pad2(minute)}`;
}

function parseYmd(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function addDays(base, days) {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
  return d;
}

function nextWeekday(from, weekday) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const diff = (weekday - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d;
}

function formatWeekdayShort(date) {
  return date
    .toLocaleDateString('pt-BR', { weekday: 'short' })
    .replace('.', '')
    .toLowerCase();
}

function formatDayMonth(date) {
  const day = date.getDate();
  const mon = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return `${day} ${mon}`;
}

function formatTimePt(hour, minute) {
  return `${Number(hour)}:${pad2(minute)}`;
}

function formatTimeInput(hour, minute) {
  return `${pad2(hour)}:${pad2(minute)}`;
}

function parseTimeInput(raw) {
  const s = String(raw || '').trim().replace(/[hH]/g, ':');
  const m = s.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = m[2] != null ? parseInt(m[2], 10) : 0;
  if (!Number.isFinite(h) || h < 0 || h > 23 || !Number.isFinite(min) || min < 0 || min > 59) {
    return null;
  }
  return { hour: pad2(h), minute: pad2(min) };
}

/** Só dígitos. Hora 00–23, minuto 00–59. Os dois-pontos entram sozinhos. */
function maskTimeText(raw, prevText) {
  let digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
  const prevDigits = String(prevText || '').replace(/\D/g, '').slice(0, 4);
  if (
    prevText &&
    digits === prevDigits &&
    String(prevText).endsWith(':') &&
    String(raw).length < String(prevText).length
  ) {
    digits = prevDigits.slice(0, -1);
  }
  let hour = '';
  let minute = '';
  for (const ch of digits) {
    const n = Number(ch);
    if (hour.length < 2) {
      if (hour.length === 0 && n > 2) {
        hour = `0${ch}`;
        continue;
      }
      if (Number(hour + ch) > 23) continue;
      hour += ch;
      continue;
    }
    if (minute.length >= 2) break;
    if (minute.length === 0 && n > 5) continue;
    if (Number(minute + ch) > 59) continue;
    minute += ch;
  }
  if (hour.length < 2) return hour;
  return minute ? `${hour}:${minute}` : `${hour}:`;
}

function onTimeKeyDown(e) {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key.length === 1 && !/\d/.test(e.key)) e.preventDefault();
}

function buildMonthCells(year, month) {
  const first = new Date(year, month, 1);
  const leading = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  const prev = new Date(year, month, 0);
  const prevDays = prev.getDate();
  for (let i = leading - 1; i >= 0; i -= 1) {
    cells.push({
      year: prev.getFullYear(),
      month: prev.getMonth(),
      day: prevDays - i,
      outside: true,
    });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ year, month, day, outside: false });
  }
  let next = new Date(year, month + 1, 1);
  while (cells.length < 42) {
    cells.push({
      year: next.getFullYear(),
      month: next.getMonth(),
      day: next.getDate(),
      outside: true,
    });
    next.setDate(next.getDate() + 1);
  }
  return cells;
}

function getShortcuts(now = new Date()) {
  const later = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  const tomorrow = addDays(now, 1);
  const thisWeekend = nextWeekday(now, 6);
  const nextWeek = addDays(now, 7);
  const nextWeekend = nextWeekday(addDays(now, 1), 6);
  if (nextWeekend.getTime() <= thisWeekend.getTime()) {
    nextWeekend.setDate(nextWeekend.getDate() + 7);
  }
  const in2w = addDays(now, 14);
  const in4w = addDays(now, 28);
  return [
    {
      id: 'today',
      label: 'Hoje',
      hint: formatWeekdayShort(now),
      apply: () => ({ date: toYmd(now), hour: pad2(now.getHours()), minute: pad2(now.getMinutes()) }),
    },
    {
      id: 'later',
      label: 'Mais tarde',
      hint: formatTimePt(later.getHours(), later.getMinutes()),
      apply: () => ({
        date: toYmd(later),
        hour: pad2(later.getHours()),
        minute: pad2(later.getMinutes()),
      }),
    },
    {
      id: 'tomorrow',
      label: 'Amanhã',
      hint: formatWeekdayShort(tomorrow),
      apply: () => ({ date: toYmd(tomorrow), hour: '09', minute: '00' }),
    },
    {
      id: 'weekend',
      label: 'Este final de semana',
      hint: formatWeekdayShort(thisWeekend),
      apply: () => ({ date: toYmd(thisWeekend), hour: '09', minute: '00' }),
    },
    {
      id: 'nextWeek',
      label: 'Semana que vem',
      hint: formatDayMonth(nextWeek),
      apply: () => ({ date: toYmd(nextWeek), hour: '09', minute: '00' }),
    },
    {
      id: 'nextWeekend',
      label: 'Próximo final de semana',
      hint: formatDayMonth(nextWeekend),
      apply: () => ({ date: toYmd(nextWeekend), hour: '09', minute: '00' }),
    },
    {
      id: '2w',
      label: '2 semanas',
      hint: formatDayMonth(in2w),
      apply: () => ({ date: toYmd(in2w), hour: '09', minute: '00' }),
    },
    {
      id: '4w',
      label: '4 semanas',
      hint: formatDayMonth(in4w),
      apply: () => ({ date: toYmd(in4w), hour: '09', minute: '00' }),
    },
  ];
}

function conflictDetail(slot) {
  if (!slot) return null;
  const startParts = splitLocal(toLocalInputValue(slot.publishAt), '09', '00');
  const endParts = splitLocal(toLocalInputValue(slot.expiresAt), '17', '00');
  const startTxt = startParts.date
    ? `${formatDayMonth(parseYmd(startParts.date))}, ${formatTimePt(startParts.hour, startParts.minute)}`
    : '—';
  const endTxt = endParts.date
    ? `${formatDayMonth(parseYmd(endParts.date))}, ${formatTimePt(endParts.hour, endParts.minute)}`
    : startParts.date
      ? `${formatDayMonth(parseYmd(startParts.date))}, 23:59`
      : 'sem término';
  const kind = slot.status === 'published' ? 'em andamento' : 'agendado';
  return {
    title: String(slot.title || 'Sem título').trim() || 'Sem título',
    kind,
    startTxt,
    endTxt,
  };
}

export default function CustomersNoticeScheduleDates({
  publishAt,
  expiresAt,
  onChange,
  scheduledSlots = [],
  onOpen,
}) {
  const btnRef = useRef(null);
  const modalRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [activeField, setActiveField] = useState('start'); // start | end
  const [start, setStart] = useState(() => splitLocal(publishAt, '09', '00'));
  const [end, setEnd] = useState(() => splitLocal(expiresAt, '17', '00'));
  const [startTimeText, setStartTimeText] = useState('09:00');
  const [endTimeText, setEndTimeText] = useState('17:00');
  const [viewMonth, setViewMonth] = useState(() => {
    const d = parseYmd(splitLocal(publishAt).date) || new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const shortcuts = useMemo(() => getShortcuts(new Date()), [open]);

  useEffect(() => {
    if (!open) return undefined;
    const s = splitLocal(publishAt, '09', '00');
    const e = splitLocal(expiresAt, '17', '00');
    setStart(s);
    setEnd(e);
    setStartTimeText(formatTimeInput(s.hour, s.minute));
    setEndTimeText(formatTimeInput(e.hour, e.minute));
    const seed = parseYmd(s.date || e.date) || new Date();
    setViewMonth({ year: seed.getFullYear(), month: seed.getMonth() });
    setActiveField('start');
    return undefined;
  }, [open, publishAt, expiresAt]);

  useEffect(() => {
    if (!open) return undefined;
    const el = btnRef.current;
    if (!el) return undefined;
    const rect = el.getBoundingClientRect();
    const width = Math.min(448, window.innerWidth - 24);
    let left = rect.left;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    if (spaceBelow >= 320) {
      setPosition({ top: rect.bottom + 8, left, bottom: 'auto' });
    } else {
      setPosition({ bottom: window.innerHeight - rect.top + 8, left, top: 'auto' });
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (modalRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const applyField = (patch) => {
    if (activeField === 'start') {
      setStart((prev) => ({ ...prev, ...patch }));
      if (patch.hour != null || patch.minute != null) {
        const next = { ...start, ...patch };
        setStartTimeText(formatTimeInput(next.hour, next.minute));
      }
    } else {
      setEnd((prev) => ({ ...prev, ...patch }));
      if (patch.hour != null || patch.minute != null) {
        const next = { ...end, ...patch };
        setEndTimeText(formatTimeInput(next.hour, next.minute));
      }
    }
  };

  const onSelectDay = (ymd) => {
    if (!start.date || (start.date && end.date)) {
      setStart((prev) => ({ ...prev, date: ymd }));
      setEnd((prev) => ({ ...prev, date: null }));
      setActiveField('end');
      return;
    }
    if (ymd < start.date) {
      setEnd((prev) => ({ ...prev, date: start.date }));
      setStart((prev) => ({ ...prev, date: ymd }));
    } else {
      setEnd((prev) => ({ ...prev, date: ymd }));
    }
  };

  const commitTimesAndClose = () => {
    const st = parseTimeInput(startTimeText) || { hour: start.hour, minute: start.minute };
    const et = parseTimeInput(endTimeText) || { hour: end.hour, minute: end.minute };
    const nextStart = { ...start, ...st };
    let nextEnd = { ...end, ...et };
    if (nextStart.date && nextEnd.date) {
      const candStart = localPartsToDate(nextStart.date, nextStart.hour, nextStart.minute);
      const candEnd = localPartsToDate(nextEnd.date, nextEnd.hour, nextEnd.minute);
      // Fim antigo de aviso encerrado (no passado) não pode ficar colado ao novo início.
      if (candEnd && candStart && candEnd.getTime() <= candStart.getTime()) {
        nextEnd = { date: '', hour: '17', minute: '00' };
      }
    }
    if (nextStart.date) {
      const candStart = localPartsToDate(nextStart.date, nextStart.hour, nextStart.minute);
      const candEnd = nextEnd.date
        ? localPartsToDate(nextEnd.date, nextEnd.hour, nextEnd.minute)
        : endOfLocalDay(nextStart.date);
      const hit = findDisplayWindowConflict(
        { publishAt: candStart, expiresAt: candEnd },
        scheduledSlots || []
      );
      if (hit) return;
    }
    onChange({
      publishAt: joinLocal(nextStart.date, nextStart.hour, nextStart.minute),
      expiresAt: joinLocal(nextEnd.date, nextEnd.hour, nextEnd.minute),
    });
    close();
  };

  const clearAll = () => {
    onChange({ publishAt: '', expiresAt: '' });
    close();
  };

  const cells = useMemo(
    () => buildMonthCells(viewMonth.year, viewMonth.month),
    [viewMonth.year, viewMonth.month]
  );
  const todayYmd = toYmd(new Date());
  const rangeStart = start.date;
  const rangeEnd = end.date;

  const busyByDay = useMemo(() => {
    const map = new Map();
    (scheduledSlots || []).forEach((slot) => {
      // marca dias tocados pela janela (até ~62 dias à frente do início)
      const startIso = slot.publishAt;
      if (!startIso) return;
      const start = new Date(startIso);
      if (Number.isNaN(start.getTime())) return;
      for (let i = 0; i < 62; i += 1) {
        const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const ymd = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
        if (!slotTouchesLocalDay(slot, ymd)) {
          if (i > 0) break;
          continue;
        }
        const list = map.get(ymd) || [];
        list.push(slot);
        map.set(ymd, list);
      }
    });
    return map;
  }, [scheduledSlots]);

  const scheduleConflict = useMemo(() => {
    if (!start.date) return null;
    const parsedStart = parseTimeInput(startTimeText);
    const parsedEnd = parseTimeInput(endTimeText);
    const hour = parsedStart?.hour || start.hour;
    const minute = parsedStart?.minute || start.minute;
    const endHour = parsedEnd?.hour || end.hour;
    const endMinute = parsedEnd?.minute || end.minute;
    const candStart = localPartsToDate(start.date, hour, minute);
    const candEnd = end.date
      ? localPartsToDate(end.date, endHour, endMinute)
      : endOfLocalDay(start.date);
    return findDisplayWindowConflict(
      { publishAt: candStart, expiresAt: candEnd },
      scheduledSlots || []
    );
  }, [
    scheduledSlots,
    start.date,
    start.hour,
    start.minute,
    end.date,
    end.hour,
    end.minute,
    startTimeText,
    endTimeText,
  ]);

  const conflictInfo = useMemo(() => conflictDetail(scheduleConflict), [scheduleConflict]);

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        if (typeof onOpen === 'function') onOpen();
        requestAnimationFrame(() => btnRef.current?.blur());
      }
      return next;
    });
  };

  const committedStart = splitLocal(publishAt, '09', '00');
  const committedEnd = splitLocal(expiresAt, '17', '00');
  const startLabel = committedStart.date
    ? `${formatDayMonth(parseYmd(committedStart.date))} ${formatTimePt(committedStart.hour, committedStart.minute)}`
    : 'Início';
  const endLabel = committedEnd.date
    ? `${formatDayMonth(parseYmd(committedEnd.date))} ${formatTimePt(committedEnd.hour, committedEnd.minute)}`
    : 'Término';

  return (
    <div className="cmc-notice-dates">
      <CustomersNoticeTip
        label="Defina início e término da exibição. O período não pode sobrepor outro aviso agendado ou em andamento."
        placement="bottom"
        wrap
        disabled={open}
      >
        <button
          ref={btnRef}
          type="button"
          className="cmc-notice-dates-trigger"
          onClick={toggleOpen}
        >
          <span className="cmc-notice-dates-trigger-left">
            <Calendar size={15} strokeWidth={1.75} aria-hidden />
            <span>Datas</span>
          </span>
          <span className="cmc-notice-dates-trigger-range">
            <span className={'cmc-notice-dates-pill' + (publishAt ? ' has-value' : '')}>
              <CalendarPlus size={14} strokeWidth={1.75} aria-hidden />
              {startLabel}
            </span>
            <span className="cmc-notice-dates-arrow" aria-hidden>
              →
            </span>
            <span className={'cmc-notice-dates-pill' + (expiresAt ? ' has-value' : '')}>
              <CalendarPlus size={14} strokeWidth={1.75} aria-hidden />
              {endLabel}
            </span>
          </span>
        </button>
      </CustomersNoticeTip>

      {open && position
        ? createPortal(
            <div
              ref={modalRef}
              className="cmc-notice-dates-float"
              style={{
                position: 'fixed',
                zIndex: 10040,
                width: Math.min(448, window.innerWidth - 24),
                ...(position.top != null && position.top !== 'auto'
                  ? { top: `${position.top}px` }
                  : { top: 'auto' }),
                ...(position.bottom != null && position.bottom !== 'auto'
                  ? { bottom: `${position.bottom}px` }
                  : { bottom: 'auto' }),
                left: `${position.left}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="cmc-notice-dates-popover"
                role="dialog"
                aria-modal="true"
                aria-label="Datas do aviso"
              >
              <div className="cmc-notice-dates-head">
                <div className="cmc-notice-dates-head-dates">
                  <button
                    type="button"
                    className={
                      'cmc-notice-dates-head-field' + (activeField === 'start' ? ' is-active' : '')
                    }
                    onClick={() => setActiveField('start')}
                  >
                    <Calendar size={14} strokeWidth={1.75} aria-hidden />
                    <span>
                      {start.date ? formatDayMonth(parseYmd(start.date)) : 'Data inicial'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={
                      'cmc-notice-dates-head-field' + (activeField === 'end' ? ' is-active' : '')
                    }
                    onClick={() => setActiveField('end')}
                  >
                    <Calendar size={14} strokeWidth={1.75} aria-hidden />
                    <span>
                      {end.date ? formatDayMonth(parseYmd(end.date)) : 'Data de término'}
                    </span>
                  </button>
                </div>
                <div className="cmc-notice-dates-time-row">
                  <input
                    className={
                      'cmc-notice-dates-time-input' + (scheduleConflict ? ' is-conflict' : '')
                    }
                    value={startTimeText}
                    inputMode="numeric"
                    maxLength={5}
                    onKeyDown={onTimeKeyDown}
                    onChange={(e) => {
                      const value = maskTimeText(e.target.value, startTimeText);
                      setStartTimeText(value);
                      if (value.length === 5) {
                        const parsed = parseTimeInput(value);
                        if (parsed) setStart((p) => ({ ...p, ...parsed }));
                      }
                    }}
                    onBlur={() => {
                      const parsed = parseTimeInput(startTimeText);
                      if (parsed) {
                        setStart((p) => ({ ...p, ...parsed }));
                        setStartTimeText(formatTimeInput(parsed.hour, parsed.minute));
                      } else {
                        setStartTimeText(formatTimeInput(start.hour, start.minute));
                      }
                    }}
                    aria-label="Horário inicial"
                    placeholder="09:00"
                  />
                  <span className="cmc-notice-dates-time-para">até</span>
                  <input
                    className="cmc-notice-dates-time-input"
                    value={endTimeText}
                    inputMode="numeric"
                    maxLength={5}
                    onKeyDown={onTimeKeyDown}
                    onChange={(e) => {
                      const value = maskTimeText(e.target.value, endTimeText);
                      setEndTimeText(value);
                      if (value.length === 5) {
                        const parsed = parseTimeInput(value);
                        if (parsed) setEnd((p) => ({ ...p, ...parsed }));
                      }
                    }}
                    onBlur={() => {
                      const parsed = parseTimeInput(endTimeText);
                      if (parsed) {
                        setEnd((p) => ({ ...p, ...parsed }));
                        setEndTimeText(formatTimeInput(parsed.hour, parsed.minute));
                      } else {
                        setEndTimeText(formatTimeInput(end.hour, end.minute));
                      }
                    }}
                    aria-label="Horário final"
                    placeholder="17:00"
                  />
                </div>
              </div>

              <div className="cmc-notice-dates-body">
                <aside className="cmc-notice-dates-shortcuts">
                  {shortcuts.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="cmc-notice-dates-shortcut"
                      onClick={() => {
                        const next = item.apply();
                        applyField(next);
                        const d = parseYmd(next.date);
                        if (d) setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
                      }}
                    >
                      <span>{item.label}</span>
                      <span className="cmc-notice-dates-shortcut-hint">{item.hint}</span>
                    </button>
                  ))}
                </aside>

                <div className="cmc-notice-dates-calendar">
                  <div className="cmc-notice-dates-cal-head">
                    <div className="cmc-notice-dates-cal-title">
                      {MONTHS[viewMonth.month]} {viewMonth.year}
                    </div>
                    <div className="cmc-notice-dates-cal-nav">
                      <button
                        type="button"
                        className="cmc-notice-dates-today-btn"
                        onClick={() => {
                          const n = new Date();
                          setViewMonth({ year: n.getFullYear(), month: n.getMonth() });
                          applyField({
                            date: toYmd(n),
                            hour: pad2(n.getHours()),
                            minute: pad2(n.getMinutes()),
                          });
                        }}
                      >
                        Hoje
                      </button>
                      <button
                        type="button"
                        className="cmc-notice-dates-nav-btn"
                        aria-label="Mês anterior"
                        onClick={() =>
                          setViewMonth((v) => {
                            const d = new Date(v.year, v.month - 1, 1);
                            return { year: d.getFullYear(), month: d.getMonth() };
                          })
                        }
                      >
                        <ChevronUp size={15} strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        className="cmc-notice-dates-nav-btn"
                        aria-label="Próximo mês"
                        onClick={() =>
                          setViewMonth((v) => {
                            const d = new Date(v.year, v.month + 1, 1);
                            return { year: d.getFullYear(), month: d.getMonth() };
                          })
                        }
                      >
                        <ChevronDown size={15} strokeWidth={2} />
                      </button>
                    </div>
                  </div>

                  <div className="cmc-notice-dates-weekdays">
                    {WEEKDAYS.map((w) => (
                      <span key={w}>{w}</span>
                    ))}
                  </div>
                  <div className="cmc-notice-dates-days">
                    {cells.map((cell, idx) => {
                      const ymd = `${cell.year}-${pad2(cell.month + 1)}-${pad2(cell.day)}`;
                      const isStart = rangeStart === ymd;
                      const isEnd = rangeEnd === ymd;
                      const inRange =
                        !!rangeStart &&
                        !!rangeEnd &&
                        ymd > rangeStart &&
                        ymd < rangeEnd;
                      const isToday = todayYmd === ymd;
                      const edge = isStart || isEnd;
                      const dayBusy = busyByDay.has(ymd);
                      return (
                        <button
                          key={`${ymd}-${idx}`}
                          type="button"
                          className={[
                            'cmc-notice-dates-day',
                            cell.outside ? 'is-outside' : '',
                            isStart ? 'is-range-start' : '',
                            isEnd ? 'is-range-end' : '',
                            inRange ? 'is-in-range' : '',
                            edge ? 'is-selected' : '',
                            isToday && !edge ? 'is-today' : '',
                            dayBusy ? 'has-busy' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => onSelectDay(ymd)}
                        >
                          {cell.day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="cmc-notice-dates-footer">
                <div className="cmc-notice-dates-actions">
                  <button
                    type="button"
                    className="user-form-link-btn user-form-footer-btn"
                    onClick={clearAll}
                  >
                    Limpar
                  </button>
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn"
                    disabled={(!start.date && !end.date) || !!scheduleConflict}
                    aria-disabled={(!start.date && !end.date) || !!scheduleConflict}
                    onClick={(e) => {
                      if (scheduleConflict) {
                        e.preventDefault();
                        return;
                      }
                      commitTimesAndClose();
                    }}
                  >
                    Aplicar
                  </button>
                </div>
              </div>
              </div>
              {scheduleConflict && conflictInfo ? (
                <div className="cmc-notice-dates-conflict-panel" role="alert">
                  <strong>Horário indisponível.</strong> Conflito com “{conflictInfo.title}” (
                  {conflictInfo.kind}): {conflictInfo.startTxt} → {conflictInfo.endTxt}. Escolha
                  outro dia ou horário.
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
